import React from 'react';
import { theme } from 'antd';
import { ChartDataset } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';

import { numberFormatter } from '~library/numbers';
import { getXAxisConfig, getKPIChartDatasetOptions } from '../Chart';
import { getKPIComparison } from '../components/KPIComparison';
import { type WidgetLoadError } from '~features/empty-states';
import KPICard from '../components/KPICard';
import KPICardContent from '../components/KPICardContent';
import { useIsPrinting } from '~library/print';

function KPIPagesCrawledContents({ rawData }: { rawData: AggregatedMetrics.PagesCrawledSummaryResult }) {
  const { token } = theme.useToken();
  const { isPrinting } = useIsPrinting();

  const comparison = React.useMemo(
    () => getKPIComparison(rawData.vCurrent, rawData.vPrevious),
    [rawData.vCurrent, rawData.vPrevious]
  );

  const dataset = React.useMemo(() => {
    const data = rawData.data.reduce<{
      labels: Date[];
      datasets: ChartDataset<'line'>[];
    }>(
      (acc, item) => {
        acc.labels.push(new Date(item.time));
        acc.datasets[0].data.push(item.count);
        return acc;
      },
      { labels: [], datasets: [{ data: [] }] }
    );
    let lineColor = token.colorText;
    if (comparison.kind === 'delta') {
      if (comparison.percentage > 0) lineColor = token.colorSuccess;
      else if (comparison.percentage < 0) lineColor = token.colorError;
    }

    Object.assign(data.datasets[0], getKPIChartDatasetOptions(lineColor));

    return data;
  }, [comparison, rawData, token.colorError, token.colorSuccess, token.colorText]);

  const percentageColor =
    comparison.kind === 'delta' && comparison.percentage > 0 ? 'success' : 'danger';

  return (
    <KPICardContent
      comparison={comparison}
      percentageColor={percentageColor}
      mainValue={numberFormatter.format(rawData.vCurrent || 0)}
    >
      <Line
        data={dataset}
        options={{
          animation: isPrinting ? false : undefined,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ...getXAxisConfig(rawData.granularity), display: false },
            y: {
              display: false,
            },
          },
          plugins: {
            tooltip: {
              enabled: false,
            },
          },
        }}
      />
    </KPICardContent>
  );
}

export const TOOLTIP_MESSAGE =
  'The number of pages that crawlers have visited on your site. More pages crawled means better visibility in search results.';

export type KPIPagesCrawledProps = {
  isLoading: boolean;
  error?: WidgetLoadError;
  rawData?: AggregatedMetrics.PagesCrawledSummaryResult;
}

function KPIPagesCrawled({ isLoading, rawData, error }: KPIPagesCrawledProps) {
  return (
    <KPICard title="Pages Crawled" titleTooltip={TOOLTIP_MESSAGE} isLoading={isLoading} error={error}>
      {rawData && <KPIPagesCrawledContents rawData={rawData} />}
    </KPICard>
  );
}

export default KPIPagesCrawled;
